import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { getTierByName, getUserTierInfo } from '../lib/tierService';

interface TierFeatures {
  maxGenerationsPerMonth: number;
  refillsOnBillingCycle: boolean;
  commissionRate: number;
  featuredListingPricePerWeek: number;
  canSellOnMarketplace: boolean;
  hasPrioritySupport: boolean;
  hasApiAccess: boolean;
  hasBulkImport: boolean;
}

interface SubscriptionData {
  tier: string;
  displayName: string;
  status: string;
  stripeSubscriptionId: string | null;
  currentPeriodStart: Date | null;
  currentPeriodEnd: Date | null;
  cancelAtPeriodEnd: boolean;
  features: TierFeatures;
  generationsUsed: number;
  generationsRemaining: number;
  refillPacksAvailable: number;
}

interface SubscriptionContextType {
  subscription: SubscriptionData | null;
  loading: boolean;
  error: string | null;
  isPremium: boolean;
  canGenerate: boolean;
  refetchSubscription: () => Promise<void>;
  upgradePlan: (tierName: string, billingInterval?: 'monthly' | 'yearly') => Promise<void>;
  downgradePlan: (tierName: string) => Promise<void>;
  cancelSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptionData = useCallback(async () => {
    if (!user?.id) {
      const freeTier = await getTierByName('free');
      const monthlyGens = freeTier?.features?.monthly_generations ? parseInt(freeTier.features.monthly_generations) : 10;
      const refillsOnBillingCycle = freeTier?.features?.refills_on_billing_cycle === 'true';

      setSubscription({
        tier: 'free',
        displayName: 'Free',
        status: 'active',
        stripeSubscriptionId: null,
        currentPeriodStart: null,
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false,
        features: {
          maxGenerationsPerMonth: monthlyGens,
          refillsOnBillingCycle,
          commissionRate: 0.30,
          featuredListingPricePerWeek: 5,
          canSellOnMarketplace: true,
          hasPrioritySupport: false,
          hasApiAccess: false,
          hasBulkImport: false,
        },
        generationsUsed: 0,
        generationsRemaining: 10,
        refillPacksAvailable: 0,
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const tierInfo = await getUserTierInfo(user.id);

      if (!tierInfo) {
        throw new Error('Failed to fetch tier information');
      }

      const { data: subscriptionData } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .maybeSingle();

      const currentTier = tierInfo.tier;
      const monthlyGens = currentTier.features?.monthly_generations
        ? (currentTier.features.monthly_generations === 'unlimited' ? 9999 : parseInt(currentTier.features.monthly_generations))
        : 10;
      const refillsOnBillingCycle = currentTier.features?.refills_on_billing_cycle === 'true';
      const commissionRate = currentTier.features?.marketplace_commission
        ? parseInt(currentTier.features.marketplace_commission) / 100
        : 0.30;
      const featuredPrice = currentTier.features?.featured_listing_price
        ? parseInt(currentTier.features.featured_listing_price)
        : 5;

      setSubscription({
        tier: currentTier.name,
        displayName: currentTier.display_name,
        status: subscriptionData?.status || 'active',
        stripeSubscriptionId: subscriptionData?.stripe_subscription_id || null,
        currentPeriodStart: subscriptionData?.current_period_start ? new Date(subscriptionData.current_period_start) : null,
        currentPeriodEnd: subscriptionData?.current_period_end ? new Date(subscriptionData.current_period_end) : null,
        cancelAtPeriodEnd: subscriptionData?.cancel_at_period_end || false,
        features: {
          maxGenerationsPerMonth: monthlyGens,
          refillsOnBillingCycle,
          commissionRate,
          featuredListingPricePerWeek: featuredPrice,
          canSellOnMarketplace: true,
          hasPrioritySupport: currentTier.features?.support_level === 'priority',
          hasApiAccess: currentTier.features?.white_label_api === 'true',
          hasBulkImport: false,
        },
        generationsUsed: tierInfo.quota?.generations_used || 0,
        generationsRemaining: tierInfo.quota?.remaining_generations || 0,
        refillPacksAvailable: tierInfo.quota?.refilled_packs || 0,
      });

      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription');
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  const upgradePlan = async (tierName: string, billingInterval: 'monthly' | 'yearly' = 'monthly') => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription/upgrade`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({ tierName, billingInterval }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upgrade plan');
      }

      const data = await response.json();

      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        await fetchSubscriptionData();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upgrade plan');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const downgradePlan = async (tierName: string) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription/downgrade`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({ tierName }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to downgrade plan');
      }

      await fetchSubscriptionData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to downgrade plan');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async () => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscription/cancel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      await fetchSubscriptionData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const isPremium = subscription ? subscription.tier !== 'free' : false;
  const canGenerate = subscription ? subscription.generationsRemaining > 0 : false;

  const value: SubscriptionContextType = {
    subscription,
    loading,
    error,
    isPremium,
    canGenerate,
    refetchSubscription: fetchSubscriptionData,
    upgradePlan,
    downgradePlan,
    cancelSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
